import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import {
  VISITAS_AGENDA_DIA_KEYS,
  VISITAS_AGENDA_DIA_LABELS,
  VisitasAgendaFila,
  emptyVisitasAgendaDias,
} from '../../../types/visitasAgenda';
import {
  DEFAULT_AGENDA_ID,
  DEFAULT_AGENDA_NOMBRE,
  loadVisitasAgendaFilas,
  saveVisitasAgendaFilas,
} from '../../../services/visitasAgendaStorage';
import { parseVisitasAgendaExcel } from '../../../services/visitasAgendaImport';
import { loadDireccionesForVisitRow } from './planVisitaRowLoaders';
import { getApiErrorMessage } from '../../../utils/apiError';

const newRowId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `fila-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const serializeFilasSnapshot = (filas: VisitasAgendaFila[]): string =>
  JSON.stringify(
    filas.map((f) => ({
      idVisitasAgenda: f.idVisitasAgenda,
      nroCliente: f.nroCliente,
      codigoDireccion: f.codigoDireccion ?? undefined,
      nombreDisplay: f.nombreDisplay,
      dias: { ...emptyVisitasAgendaDias(), ...f.dias },
    })),
  );

const TABLE_COL_SPAN = 9;

const clienteCellSx = {
  width: '26%',
  minWidth: 160,
  maxWidth: 320,
  overflow: 'hidden',
} as const;

const diaCellSx = {
  width: 88,
  minWidth: 72,
  maxWidth: 96,
  px: 0.5,
  whiteSpace: 'nowrap',
} as const;

const VisitasAgendaTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [draftFilas, setDraftFilas] = useState<VisitasAgendaFila[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const loadAgendaDraft = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSelectionMode(false);
    setSelectedIds(new Set());
    try {
      const filas = await loadVisitasAgendaFilas();
      const normalized = filas.map((f) => ({
        ...f,
        dias: { ...emptyVisitasAgendaDias(), ...f.dias },
      }));
      setDraftFilas(normalized);
      setSavedSnapshot(serializeFilasSnapshot(normalized));
    } catch (error: unknown) {
      setLoadError(getApiErrorMessage(error, 'No se pudo cargar la agenda.'));
      setDraftFilas([]);
      setSavedSnapshot(serializeFilasSnapshot([]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAgendaDraft();
  }, [loadAgendaDraft]);

  const isDirty = useMemo(
    () => serializeFilasSnapshot(draftFilas) !== savedSnapshot,
    [draftFilas, savedSnapshot],
  );

  const handleToggleDia = (rowIndex: number, dia: keyof VisitasAgendaFila['dias']) => {
    setDraftFilas((prev) =>
      prev.map((row, i) =>
        i === rowIndex ? { ...row, dias: { ...row.dias, [dia]: !row.dias[dia] } } : row,
      ),
    );
  };

  const handleRemoveRow = (rowIndex: number) => {
    setDraftFilas((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleToggleSelectionMode = () => {
    if (selectionMode) {
      exitSelectionMode();
      return;
    }
    setSelectionMode(true);
  };

  const handleSelectAll = () => {
    setSelectedIds(new Set(draftFilas.map((f) => f.idVisitasAgenda)));
  };

  const handleToggleRowSelected = (idVisitasAgenda: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(idVisitasAgenda)) {
        next.delete(idVisitasAgenda);
      } else {
        next.add(idVisitasAgenda);
      }
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) {
      enqueueSnackbar('Seleccione al menos un cliente.', { variant: 'warning' });
      return;
    }
    if (
      !window.confirm(
        `¿Eliminar ${selectedIds.size} cliente${selectedIds.size === 1 ? '' : 's'} de la agenda?`,
      )
    ) {
      return;
    }
    setDraftFilas((prev) => prev.filter((f) => !selectedIds.has(f.idVisitasAgenda)));
    exitSelectionMode();
  };

  const handleClienteSelected = async (cliente: {
    nroCliente: number;
    nombre?: string;
    razonSocial?: string;
  }) => {
    const nombreDisplay =
      [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') ||
      `Cliente ${cliente.nroCliente}`;
    const dirPatch = await loadDireccionesForVisitRow(cliente.nroCliente);
    const nueva: VisitasAgendaFila = {
      idVisitasAgenda: newRowId(),
      nroCliente: cliente.nroCliente,
      codigoDireccion: dirPatch.codigoDireccion,
      nombreDisplay,
      dias: emptyVisitasAgendaDias(),
    };
    if (dirPatch.direccionLabel) {
      nueva.nombreDisplay = `${nombreDisplay} — ${dirPatch.direccionLabel}`;
    }
    setDraftFilas((prev) => [...prev, nueva]);
    setClienteSearchOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveVisitasAgendaFilas(DEFAULT_AGENDA_ID, draftFilas);
      if (!saved) {
        enqueueSnackbar('No se pudo guardar la agenda.', { variant: 'error' });
        return;
      }
      setDraftFilas(saved.filas);
      setSavedSnapshot(serializeFilasSnapshot(saved.filas));
      enqueueSnackbar(`Agenda "${DEFAULT_AGENDA_NOMBRE}" guardada (${saved.filas.length} filas).`, {
        variant: 'success',
      });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar la agenda.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleImportFile = async (file: File) => {
    setImporting(true);
    try {
      const result = await parseVisitasAgendaExcel(file);
      if (result.filas.length === 0) {
        const detalle =
          result.errores.length > 0
            ? result.errores
                .slice(0, 3)
                .map((e) => `Fila ${e.fila}: ${e.mensaje}`)
                .join(' — ')
            : 'Revise columnas Nro Cuenta y FRECUENCIA.';
        enqueueSnackbar(detalle, { variant: 'warning', autoHideDuration: 8000 });
        return;
      }
      if (
        !window.confirm(
          `Se importarán ${result.filas.length} filas y se reemplazará toda la agenda al guardar. ¿Continuar?`,
        )
      ) {
        return;
      }
      const importedFilas: VisitasAgendaFila[] = result.filas.map((f) => ({
        ...f,
        idVisitasAgenda: newRowId(),
        dias: { ...emptyVisitasAgendaDias(), ...f.dias },
      }));
      setDraftFilas(importedFilas);
      const msg =
        result.errores.length > 0
          ? `Importadas ${result.filas.length} filas en pantalla. ${result.errores.length} omitidas (FRECUENCIA). Pulse Guardar cambios.`
          : `Importadas ${result.filas.length} filas. Pulse Guardar cambios para persistir en el servidor.`;
      enqueueSnackbar(msg, { variant: result.errores.length ? 'warning' : 'info' });
    } catch (error: unknown) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Error al leer el archivo Excel.',
        { variant: 'error', autoHideDuration: 6000 },
      );
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          {DEFAULT_AGENDA_NOMBRE}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={importing || loading || selectionMode}
          onClick={() => fileInputRef.current?.click()}
        >
          Importar Excel
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleImportFile(file);
          }}
        />
        {selectionMode ? (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DoneAllIcon />}
              disabled={loading || draftFilas.length === 0}
              onClick={handleSelectAll}
            >
              Seleccionar todo
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={loading || selectedIds.size === 0}
              onClick={handleDeleteSelected}
            >
              Eliminar{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={exitSelectionMode}
            >
              Cancelar
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckBoxOutlinedIcon />}
            disabled={loading || draftFilas.length === 0}
            onClick={handleToggleSelectionMode}
          >
            Seleccionar
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={loading || selectionMode}
          onClick={() => setClienteSearchOpen(true)}
        >
          Agregar cliente
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Configure qué clientes se visitan cada día de la semana. Al crear un plan, use{' '}
        <strong>Autocompletar</strong> con el vendedor (solo clientes asignados a ese vendedor). Los
        datos se guardan en el servidor.
      </Typography>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {isDirty ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hay cambios sin guardar.
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 340px)',
            overflowX: 'auto',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {selectionMode ? (
                  <TableCell padding="checkbox" sx={{ width: 48 }} />
                ) : null}
                <TableCell sx={clienteCellSx}>Cliente / dirección</TableCell>
                {VISITAS_AGENDA_DIA_KEYS.map((dia) => (
                  <TableCell key={dia} align="center" sx={diaCellSx}>
                    {VISITAS_AGENDA_DIA_LABELS[dia]}
                  </TableCell>
                ))}
                {!selectionMode ? <TableCell align="center" sx={{ width: 56 }} /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {draftFilas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_SPAN}>
                    No hay filas. Agregue clientes o importe un Excel.
                  </TableCell>
                </TableRow>
              ) : (
                draftFilas.map((row, index) => (
                  <TableRow
                    key={row.idVisitasAgenda}
                    hover
                    selected={selectionMode && selectedIds.has(row.idVisitasAgenda)}
                    onClick={
                      selectionMode
                        ? () => handleToggleRowSelected(row.idVisitasAgenda)
                        : undefined
                    }
                    sx={selectionMode ? { cursor: 'pointer' } : undefined}
                  >
                    {selectionMode ? (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(row.idVisitasAgenda)}
                          tabIndex={-1}
                          disableRipple
                          sx={{ pointerEvents: 'none' }}
                          inputProps={{ 'aria-label': `Seleccionar ${row.nombreDisplay}` }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell sx={clienteCellSx}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={row.nombreDisplay}
                        sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {row.nombreDisplay}
                      </Typography>
                      {row.codigoDireccion ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          title={row.codigoDireccion}
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {row.codigoDireccion}
                        </Typography>
                      ) : null}
                    </TableCell>
                    {VISITAS_AGENDA_DIA_KEYS.map((dia) => (
                      <TableCell key={dia} align="center" padding="checkbox" sx={diaCellSx}>
                        <Checkbox
                          size="small"
                          checked={row.dias[dia]}
                          disabled={selectionMode}
                          tabIndex={selectionMode ? -1 : undefined}
                          disableRipple={selectionMode}
                          sx={selectionMode ? { pointerEvents: 'none' } : undefined}
                          onChange={() => handleToggleDia(index, dia)}
                        />
                      </TableCell>
                    ))}
                    {!selectionMode ? (
                      <TableCell align="center">
                        <Tooltip title="Quitar fila">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveRow(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          disabled={!isDirty || saving || loading}
          onClick={() => void loadAgendaDraft()}
        >
          Descartar cambios
        </Button>
        <Button variant="contained" disabled={!isDirty || saving || loading} onClick={() => void handleSave()}>
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </Stack>

      <ClienteSearch
        open={clienteSearchOpen}
        onClose={() => setClienteSearchOpen(false)}
        onSelect={handleClienteSelected}
      />
    </Box>
  );
};

export default VisitasAgendaTab;
